import * as PIXI from 'pixi.js';

export type EndingType = 'victory' | 'gameOver';

export interface EndingScreenOptions {
  type: EndingType;
  endingName?: string;
  progress?: number;
  onRestart?: () => void;
  onGallery?: () => void;
  onShare?: () => void;
}

export class EndingScreen {
  private container: PIXI.Container;
  private backgroundSprite: PIXI.Sprite;
  private titleText: PIXI.Text;
  private captionText: PIXI.Text;
  private progressContainer: PIXI.Container;
  private progressLabel: PIXI.Text;
  private progressValue: PIXI.Text;
  
  private buttonContainer: PIXI.Container;
  private galleryButton: PIXI.Container;
  private restartButton: PIXI.Container;
  private shareButton: PIXI.Container;

  private isVisible: boolean = false;
  private screenWidth: number;
  private screenHeight: number;

  constructor(screenWidth: number, screenHeight: number) {
    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;

    // Create main container
    this.container = new PIXI.Container();
    this.container.visible = false;
    this.container.zIndex = 1000;

    // Background
    this.backgroundSprite = new PIXI.Sprite(PIXI.Texture.WHITE); // Placeholder
    this.backgroundSprite.tint = 0x000000; // Start black
    this.backgroundSprite.anchor.set(0.5);
    this.container.addChild(this.backgroundSprite);

    // Load texture explicitly
    PIXI.Assets.load('/assets/background/end_screen.png').then((texture) => {
        if (texture) {
            console.log('Ending Screen background loaded successfully');
            this.backgroundSprite.texture = texture;
            this.backgroundSprite.tint = 0xFFFFFF; // Reset tint
            this.updateLayout();
        } else {
            console.error('Failed to load Ending Screen background');
        }
    }).catch(err => {
        console.error('Error loading Ending Screen background:', err);
    });

    // Title: "THE END"
    this.titleText = new PIXI.Text('THE END', {
      fontFamily: '"Erben Gothic", serif',
      fontSize: 100,
      fill: '#D4AF37',
      align: 'center',
      padding: 8,
      dropShadow: {
        color: 0x000000,
        alpha: 0.5,
        blur: 4,
        distance: 2,
        angle: Math.PI / 4,
      },
    });
    this.titleText.resolution = window.devicePixelRatio || 2;
    this.titleText.anchor.set(0.5);
    this.container.addChild(this.titleText);

    // Caption: "You Have Reached Ending [...]"
    this.captionText = new PIXI.Text('', {
      fontFamily: 'Rosarivo, serif',
      fontSize: 32,
      fill: '#D7CDAE',
      align: 'center',
      padding: 4,
    });
    this.captionText.resolution = window.devicePixelRatio || 2;
    this.captionText.anchor.set(0.5);
    this.container.addChild(this.captionText);

    // Progress Container
    this.progressContainer = new PIXI.Container();
    this.container.addChild(this.progressContainer);

    // "Progress" Label (Beige)
    this.progressLabel = new PIXI.Text('Progress ', {
      fontFamily: 'Rosarivo, serif',
      fontSize: 48,
      fontWeight: 'bold',
      fill: '#D7CDAE', 
      align: 'center',
      padding: 4,
    });
    this.progressLabel.resolution = window.devicePixelRatio || 2;
    this.progressLabel.anchor.set(0, 0.5); 
    this.progressContainer.addChild(this.progressLabel);

    // Percentage Value (Yellow)
    this.progressValue = new PIXI.Text('0%', {
      fontFamily: 'Rosarivo, serif',
      fontSize: 48,
      fontWeight: 'bold',
      fill: '#D4AF37',
      align: 'center',
      padding: 4,
    });
    this.progressValue.resolution = window.devicePixelRatio || 2;
    this.progressValue.anchor.set(0, 0.5); 
    this.progressContainer.addChild(this.progressValue);

    // Buttons container
    this.buttonContainer = new PIXI.Container();
    this.container.addChild(this.buttonContainer);

    this.galleryButton = this.createButton('/assets/ui/gallery.png', 'Gallery', '/assets/ui/icon-gallery.png'); 
    this.restartButton = this.createButton('/assets/ui/try-again.png', 'Try Again', '/assets/ui/icon-try-again.png');
    this.shareButton = this.createButton('/assets/ui/share.png', 'Share', '/assets/ui/icon-share.png');

    this.buttonContainer.addChild(this.galleryButton);
    this.buttonContainer.addChild(this.restartButton);
    this.buttonContainer.addChild(this.shareButton);

    // Position elements
    this.updateLayout();
  }

  private createButton(imagePath: string, label: string, iconPath: string): PIXI.Container {
    const container = new PIXI.Container();
    container.eventMode = 'static'; // Pixi v7+ (or interactive for v6)
    container.cursor = 'pointer';

    const sprite = new PIXI.Sprite(PIXI.Texture.EMPTY);
    sprite.anchor.set(0.5);
    sprite.scale.set(0.4);
    container.addChild(sprite);
    
    // Load button texture explicitly
    PIXI.Assets.load(imagePath).then((texture) => {
        if (texture) {
            sprite.texture = texture;
        } else {
            console.error(`Failed to load button texture: ${imagePath}`);
        }
    }).catch(err => {
         console.error(`Error loading button texture ${imagePath}:`, err);
    });

    // Content Container
    const content = new PIXI.Container();
    container.addChild(content);

    // Icon Sprite
    const iconSprite = new PIXI.Sprite(PIXI.Texture.EMPTY);
    iconSprite.anchor.set(0, 0.5); 
    content.addChild(iconSprite);

    // Load icon texture
    PIXI.Assets.load(iconPath).then((texture) => {
        if (texture) {
            iconSprite.texture = texture;
            // Scale icon to fit height of approx 32px
            const targetHeight = 32;
            const scale = targetHeight / texture.height;
            iconSprite.scale.set(scale);
            
            // Recalculate centering after load if needed, but keeping simple for now
            // as widths likely won't change drastically for icons.
        } else {
            // console.error(`Failed to load icon texture: ${iconPath}`);
            // Silent fail or default?
            // Try explicit check
            console.log(`Failed to load icon: ${iconPath}`);
        }
    });

    // Label
    const text = new PIXI.Text(label, {
      fontFamily: 'Rosarivo, serif',
      fontSize: 24,
      fill: 0xFFFFFF,
      fontWeight: 'bold',
      align: 'center',
      padding: 4,
    });
    text.resolution = window.devicePixelRatio || 2;
    text.anchor.set(0, 0.5); 
    
    // Position
    const iconWidth = 32; // Assumed width
    const spacing = 15;
    text.x = iconWidth + spacing;
    content.addChild(text);

    // Center content
    // Pivot at center of the group
    const totalWidth = iconWidth + spacing + text.width;
    content.pivot.set(totalWidth / 2, 0);
    
    content.x = 0;
    content.y = 0;

    // Hover effects
    container.on('pointerover', () => {
      container.scale.set(1.05);
      sprite.tint = 0xDDDDDD; // Darken slightly on hover
    });
    container.on('pointerout', () => {
      container.scale.set(1);
      sprite.tint = 0xFFFFFF; // Reset tint
    });

    return container;
  }

  public show(options: EndingScreenOptions): void {
    const interactBtn = document.getElementById('interactionBtn');
    if (interactBtn) {
        interactBtn.style.display = 'none';
        interactBtn.classList.remove('visible');
    }

    // Update Content
    const endingName = options.endingName || (options.type === 'victory' ? '[Good]' : '[Bad]');
    this.captionText.text = `You Have Reached Ending ${endingName}.`;

    const progress = options.progress !== undefined ? options.progress : 0;
    this.progressValue.text = `${progress}%`;

    // Button Actions
    this.galleryButton.onclick = () => options.onGallery?.();
    this.restartButton.onclick = () => options.onRestart?.();
    this.shareButton.onclick = () => options.onShare?.();
    
    // Also bind pointertap for touch compatibility
    this.galleryButton.on('pointertap', () => options.onGallery?.());
    this.restartButton.on('pointertap', () => options.onRestart?.());
    this.shareButton.on('pointertap', () => options.onShare?.());

    this.updateLayout();
    this.isVisible = true;
    this.container.visible = true;
    this.container.alpha = 0;
    
    // Fade in
    let alpha = 0;
    const fade = () => {
      alpha += 0.05;
      this.container.alpha = alpha;
      if (alpha < 1) requestAnimationFrame(fade);
    };
    fade();
  }

  public hide(): void {
    this.isVisible = false;
    this.container.visible = false;
  
    const interactBtn = document.getElementById('interactionBtn');
    if (interactBtn) {
        interactBtn.style.display = ''; // Reset inline style
    }
  }

  private updateLayout(): void {
    const cx = this.screenWidth / 2;
    const cy = this.screenHeight / 2;

    // Background covers screen
    // Scale bg to cover
    if (this.backgroundSprite.texture.width > 1) {
        const bgRatio = this.backgroundSprite.texture.width / this.backgroundSprite.texture.height;
        const screenRatio = this.screenWidth / this.screenHeight;
        let scale = 1;
        if (screenRatio > bgRatio) {
            scale = this.screenWidth / this.backgroundSprite.texture.width;
        } else {
            scale = this.screenHeight / this.backgroundSprite.texture.height;
        }
        this.backgroundSprite.scale.set(scale);
    }
    this.backgroundSprite.position.set(cx, cy);

    // Title at Top (approx 20% from top)
    this.titleText.position.set(cx, this.screenHeight * 0.2);

    // Caption below Title
    this.captionText.position.set(cx, this.titleText.y + 80);

    // Progress in Middle
    const labelWidth = this.progressLabel.width;
    const valueWidth = this.progressValue.width;
    const totalWidth = labelWidth + valueWidth;
    
    // Position label start such that the whole block is centered
    this.progressLabel.x = -totalWidth / 2;
    this.progressLabel.y = 0;
    
    // Position value right after label
    this.progressValue.x = this.progressLabel.x + labelWidth;
    this.progressValue.y = 0;

    this.progressContainer.position.set(cx, cy);

    // Buttons at Bottom (approx 80% from top)
    this.buttonContainer.position.set(cx, this.screenHeight * 0.8);
    
    // Space buttons out more - user requested adjustment
    this.galleryButton.x = -250;
    this.restartButton.x = 0;
    this.shareButton.x = 250;
  }

  public updateScreenSize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;
    this.updateLayout();
  }

  public getContainer(): PIXI.Container {
    return this.container;
  }

  public isShowing(): boolean {
    return this.isVisible;
  }

  public destroy(): void {
    this.container.destroy({ children: true });
  }
}
